import { Injectable } from '@angular/core';
import { sortArchivedThenByName } from '@core/utils/sort-archived-then-by-name';
import { EquipmentWhereInput } from '../../../../../../core/api/equipments/equipments.type';
import { LocationSectorWhereInput } from '../../../../../../core/api/location-sectors/location-sectors.type';
import { TreeViewItem } from '../../../../../../core/components/fc-tree-view/fc-tree.types';
import { Equipment } from '../../../../../../core/interfaces';
import { LocationEnvironment } from '../../../../../../core/interfaces/locations-environments';
import { LocationSector } from '../../../../../../core/interfaces/locations-sectors';
import { EquipmentLinkService } from './services/equipment/equipment.service';
import { LocationLinkEnvironmentService } from './services/location-environment/location-environment.service';
import { LocationLinkSectorService } from './services/location-sector/location-sector.service';
import { LinkSectorsEnvironmentsEquipmentsParams, LinkSectorsEnvironmentsEquipmentsResult } from './tab-links.types';

@Injectable({
  providedIn: 'root'
})
export class LocationTabLinksService {

  public constructor(
    private readonly locationLinkSectorService: LocationLinkSectorService,
    private readonly equipmentLinkService: EquipmentLinkService,
    private readonly locationLinkEnvironmentService: LocationLinkEnvironmentService,
  ) {}

  public linkSectorsEnvironmentsEquipments(params: LinkSectorsEnvironmentsEquipmentsParams): LinkSectorsEnvironmentsEquipmentsResult {
    const { sectors, environments, equipments } = params;

    const environmentsBySectorMap: Record<string, LocationEnvironment[]> = {};
    environments.forEach((environment) => {
      environmentsBySectorMap[environment.locationSector.id] = environmentsBySectorMap[environment.locationSector.id] || [];
      environmentsBySectorMap[environment.locationSector.id].push(environment);
    });

    const unlinkedEquipments: Equipment[] = [];
    const equipmentsBySectorMap: Record<string, Equipment[]> = {};
    const equipmentsByEnvironmentMap: Record<string, Equipment[]> = {};

    equipments.forEach((equipment) => {
      if (equipment.locationSector) {
        if (equipment.locationEnvironment) {
          equipmentsByEnvironmentMap[equipment.locationEnvironment.id] = equipmentsByEnvironmentMap[equipment.locationEnvironment.id] || [];
          equipmentsByEnvironmentMap[equipment.locationEnvironment.id].push(equipment);
        } else {
          equipmentsBySectorMap[equipment.locationSector.id] = equipmentsBySectorMap[equipment.locationSector.id] || [];
          equipmentsBySectorMap[equipment.locationSector.id].push(equipment);
        }
      } else {
        unlinkedEquipments.push(equipment);
      }
    });

    sectors.sort(sortArchivedThenByName);
    sectors.forEach((sector) => {
      sector.equipments = equipmentsBySectorMap[sector.id] || [];
      sector.equipments.sort(sortArchivedThenByName);
      Reflect.deleteProperty(equipmentsBySectorMap, sector.id);

      sector.locationEnvironments = environmentsBySectorMap[sector.id] || [];
      sector.locationEnvironments.sort(sortArchivedThenByName);

      sector.locationEnvironments.forEach((environment) => {
        environment.equipments = equipmentsByEnvironmentMap[environment.id] || [];
        Reflect.deleteProperty(equipmentsByEnvironmentMap, environment.id);
        environment.equipments.sort(sortArchivedThenByName);
      });
    });

    Object.values(equipmentsBySectorMap).forEach((equipmentsWithNoSector) => {
      unlinkedEquipments.push(...equipmentsWithNoSector);
    });

    Object.values(equipmentsByEnvironmentMap).forEach((equipmentsWithNoEnvironment) => {
      unlinkedEquipments.push(...equipmentsWithNoEnvironment);
    });

    unlinkedEquipments.sort(sortArchivedThenByName);

    return {
      sectors,
      equipments: unlinkedEquipments
    };
  }

  public async formatSectors(sectors: LocationSector[]): Promise<TreeViewItem[]> {
    return this.locationLinkSectorService.formatSectors(sectors);
  }

  public async formatUnlinkedEquipments(equipments: Equipment[]): Promise<TreeViewItem[]> {
    return this.equipmentLinkService.formatUnlinkedEquipments(equipments);
  }

  public async searchLocationSectors(params?: LocationSectorWhereInput): Promise<LocationSector[]> {
    return this.locationLinkSectorService.searchLocationSectors({ where: params });
  }

  public async searchEquipments(params?: EquipmentWhereInput): Promise<Equipment[]> {
    return this.equipmentLinkService.searchEquipments({ where: params });
  }

  public async searchEquipmentsWithWarranties(params?: EquipmentWhereInput): Promise<Equipment[]> {
    return this.equipmentLinkService.searchEquipmentsWithWarranties({ where: params });
  }

  public async searchLocationEnvironments(params?: LocationSectorWhereInput): Promise<LocationEnvironment[]> {
    return this.locationLinkEnvironmentService.searchLocationEnvironments({ where: params });
  }

  public async searchLocationSectorsWithEnvironmentsAndEquipments(params?: LocationSectorWhereInput): Promise<LocationSector[]> {
    return this.locationLinkSectorService.searchLocationSectorsWithEnvironmentsAndEquipments({ where: params });
  }
}
